import axios from 'axios';

import { route } from 'ziggy-js'
window.route = (name, params = {}, absolute = false) => route(name, params, absolute, window.Ziggy)
axios.defaults.withCredentials = true;

window.axios = axios;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Add device token header for trusted device feature
axios.interceptors.request.use(
    (config) => {
        // Add device token if available in localStorage
        const deviceToken = localStorage.getItem('device_token');
        if (deviceToken) {
            config.headers['X-Device-Token'] = deviceToken;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Global CSRF token management
let globalCsrfToken = null;
let isRefreshingCsrf = false;
let csrfRefreshPromise = null;

// Unified CSRF token refresh function
const refreshCsrfToken = async () => {
    if (isRefreshingCsrf && csrfRefreshPromise) {
        return csrfRefreshPromise;
    }

    isRefreshingCsrf = true;
    csrfRefreshPromise = (async () => {
        try {
            // Always refresh from server to get a fresh token that matches the current session
            // console.log('🔄 Refreshing CSRF token from server...');

            // Try to refresh the CSRF token using your existing endpoint
            const response = await fetch('/csrf-token', {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                },
                credentials: 'same-origin'
            });

            if (response.ok) {
                const data = await response.json();
                const newToken = data.csrf_token;

                if (newToken) {
                    globalCsrfToken = newToken;

                    // Update the meta tag with the new token
                    const metaTag = document.querySelector('meta[name="csrf-token"]');
                    if (metaTag) {
                        metaTag.setAttribute('content', newToken);
                    }

                    // Update axios default header
                    axios.defaults.headers.common['X-CSRF-TOKEN'] = newToken;

                    // console.log('✅ CSRF token refreshed successfully:', newToken);
                    return newToken;
                }
            }

            throw new Error('No CSRF token found after refresh');
        } catch (error) {
            console.error('Failed to refresh CSRF token:', error);
            throw error;
        } finally {
            isRefreshingCsrf = false;
            csrfRefreshPromise = null;
        }
    })();

    return csrfRefreshPromise;
};

// CSRF token refresh interceptor - handles 419 errors gracefully
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Global 419 error counter to prevent infinite loops
let csrfErrorCount = 0;
const MAX_CSRF_RETRIES = 3;
let isHandlingCsrfError = false; // Prevent concurrent CSRF handling

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 419 && !originalRequest._retry && !isHandlingCsrfError) {
            // Check if we've exceeded max retries
            if (csrfErrorCount >= MAX_CSRF_RETRIES) {
                console.error('Max CSRF retries exceeded, giving up');
                csrfErrorCount = 0; // Reset counter
                isHandlingCsrfError = false;
                return Promise.reject(error);
            }

            csrfErrorCount++;
            isHandlingCsrfError = true;

            if (isRefreshing) {
                // Queue the request until token is refreshed
                return new Promise(function(resolve, reject) {
                    failedQueue.push({resolve, reject});
                }).then(token => {
                    originalRequest.headers['X-CSRF-TOKEN'] = token;
                    isHandlingCsrfError = false;
                    return axios(originalRequest);
                }).catch(err => {
                    isHandlingCsrfError = false;
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Use unified CSRF token refresh function
                const newToken = await refreshCsrfToken();

                processQueue(null, newToken);
                originalRequest.headers['X-CSRF-TOKEN'] = newToken;
                csrfErrorCount = 0; // Reset counter on success
                isHandlingCsrfError = false;
                return axios(originalRequest);
            } catch (err) {
                console.error('CSRF token refresh failed:', err);
                processQueue(err, null);
                isHandlingCsrfError = false;

                // If CSRF refresh fails completely, reload the page
                if (csrfErrorCount >= MAX_CSRF_RETRIES) {
                    if (import.meta.env.DEV) console.warn('CSRF token refresh failed completely, reload page...');
                    // window.location.reload();
                }

                return Promise.reject(err);
            } finally {
                isRefreshing = false;
                isHandlingCsrfError = false;
            }
        }

        return Promise.reject(error);
    }
);

// Set up CSRF token handling
const updateCsrfToken = () => {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    if (token) {
        globalCsrfToken = token;
        window.axios.defaults.headers.common['X-CSRF-TOKEN'] = token;
    } else {
        console.error("CSRF token not found in meta tag!");
    }
};

// Initial CSRF token setup
updateCsrfToken();

// Ensure CSRF token is available before any requests
const ensureCsrfToken = async () => {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    if (!token) {
        try {
            const response = await axios.get('/csrf-token');
            const newToken = response.data.csrf_token;

            if (newToken) {
                globalCsrfToken = newToken;
                const metaTag = document.querySelector('meta[name="csrf-token"]');
                if (metaTag) {
                    metaTag.setAttribute('content', newToken);
                }
                // Update axios default header
                axios.defaults.headers.common['X-CSRF-TOKEN'] = newToken;
                updateCsrfToken();
            }
        } catch (error) {
            if (import.meta.env.DEV) console.warn('Failed to fetch CSRF token:', error);
        }
    }
};

// Call this on page load to ensure CSRF token is available
ensureCsrfToken();

// Debug: Verify new bootstrap.js is loaded
// console.log('🚀 NEW BOOTSTRAP.JS LOADED - CSRF interceptor active');

// Request interceptor to ensure CSRF token is available
axios.interceptors.request.use(
    (config) => {
        // Get token from meta tag (should be fresh after page load)
        const token = document.querySelector('meta[name="csrf-token"]')?.content;
        if (token) {
            config.headers['X-CSRF-TOKEN'] = token;
        }
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.withCredentials = true;
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Global 419 error suppression - prevent all 419 errors from showing in console
const suppress419ConsoleError = (error) => {
    if (error.response?.status === 419) {
        // Mark as silent to prevent console logging
        return Promise.reject({
            ...error,
            silent: true,
            suppressConsole: true
        });
    }
    return Promise.reject(error);
};

// Combined response interceptor to handle 419 CSRF errors and Inertia requests
axios.interceptors.response.use(
    (response) => {
        // console.log('✅ Response interceptor: SUCCESS', response.status, response.config.url);
        return response;
    },
    async (error) => {
        // console.log('❌ Response interceptor: ERROR', error.response?.status, error.config?.url);

        const originalRequest = error.config;

        // Handle 419 CSRF errors
        if (error.response?.status === 419) {
            // console.log('🔥 419 CSRF error detected, checking retry status...', {
            //     hasRetry: !!originalRequest._retry,
            //     retryCount: originalRequest._retryCount || 0
            // });

            // Allow up to 2 retries
            const retryCount = originalRequest._retryCount || 0;
            if (retryCount < 2) {
                originalRequest._retryCount = retryCount + 1;
                // console.log('🔥 Refreshing token and retrying...');

                try {
                // Refresh the CSRF token
                const newToken = await refreshCsrfToken();

                if (newToken) {
                    // Update the original request with the new token
                    originalRequest.headers['X-CSRF-TOKEN'] = newToken;

                    // console.log('🔄 Retrying request with new CSRF token...');
                    return axios(originalRequest);
                } else {
                    console.error('❌ Failed to get new CSRF token');
                    return Promise.reject(error);
                }
                } catch (refreshError) {
                    console.error('❌ Failed to refresh CSRF token:', refreshError);
                    return Promise.reject(error);
                }
            } else {
                if (import.meta.env.DEV) console.log('❌ Max retries reached, giving up');
                return Promise.reject(error);
            }
        }

        // Handle Inertia requests - suppress 419 errors for specific cases
        if (error.response?.status === 419 &&
            (error.config?.headers?.['X-Inertia'] === 'true' ||
             error.config?.headers?.['X-Inertia-Version'] !== undefined ||
             error.config?.url?.includes('/login'))) {
            if (import.meta.env.DEV) console.log('🔇 Suppressing Inertia 419 error');
            return Promise.reject({
                ...error,
                silent: true,
                suppressConsole: true
            });
        }

        // For other errors, suppress 419 console logs
        // console.log('📝 Using default error handler');
        return suppress419ConsoleError(error);
    }
);

// Use Laravel backend URL for API requests (not Vite dev server URL)
// During development, Vite runs on port 5173 but Laravel API is on port 8000
const isDevelopment = import.meta.env.DEV;
const isProduction = !isDevelopment;

// Determine backend URL based on environment
let baseURL;
if (isProduction) {
    // In production, API and frontend are on same domain/port
    baseURL = window.location.protocol + '//' + window.location.hostname +
              (window.location.port ? ':' + window.location.port : '');
} else {
    // In development, Vite serves frontend on different port than Laravel API
    // Laravel API is typically on localhost:8000 during development
    const appUrl = import.meta.env.VITE_APP_URL || 'http://localhost:8000';

    // Extract hostname and port from VITE_APP_URL
    const url = new URL(appUrl);
    const hostname = url.hostname;
    const port = url.port;

    // If we're on a subdomain, use the corresponding backend subdomain
    const currentHostname = window.location.hostname;
    let backendHostname = hostname; // Default fallback

    if (currentHostname.includes('.localhost') && currentHostname !== 'localhost') {
        // Handle subdomain case - replace first part of hostname with backend hostname
        const parts = currentHostname.split('.');
        backendHostname = currentHostname; // Use current hostname if it matches backend pattern
    }

    baseURL = window.location.protocol + '//' + backendHostname +
              (port ? ':' + port : '');
}

window.axios.defaults.baseURL = baseURL;

// Global fetch interceptor for CSRF error handling
const originalFetch = window.fetch;
window.fetch = async (input, init = {}) => {
    // Ensure we have a CSRF token
    if (!globalCsrfToken) {
        const metaToken = document.querySelector('meta[name="csrf-token"]')?.content;
        if (metaToken) {
            globalCsrfToken = metaToken;
        }
    }

    // Add CSRF token to requests that need it
    const needsCsrf = init.method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(init.method.toUpperCase());
    if (needsCsrf && globalCsrfToken) {
        init.headers = {
            ...init.headers,
            'X-CSRF-TOKEN': globalCsrfToken,
            'X-Requested-With': 'XMLHttpRequest'
        };
    }

    try {
        const response = await originalFetch(input, init);

        // Handle CSRF errors (419 status) - suppress console logging but maintain functionality
        if (response.status === 419) {
            // Don't log to console, just handle silently
            try {
                // Refresh CSRF token
                const newToken = await refreshCsrfToken();

                // Retry the original request with new token
                if (needsCsrf) {
                    init.headers = {
                        ...init.headers,
                        'X-CSRF-TOKEN': newToken
                    };
                }

                return await originalFetch(input, init);
            } catch (error) {
                // Only log if it's not a 419 error (to avoid infinite loops)
                if (error.response?.status !== 419) {
                    console.error('Failed to refresh CSRF token for fetch request:', error);
                }
                // Re-throw the error to maintain proper error handling
                // The console suppression will catch it at a higher level
                throw error;
            }
        }

        return response;
    } catch (error) {
        // Re-throw non-CSRF errors
        throw error;
    }
};

// Global unhandled rejection handler for CSRF errors - suppress console logging
window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.response?.status === 419) {
        // Suppress console logging for 419 errors
        event.preventDefault();

        // Use our unified CSRF refresh function silently
        refreshCsrfToken()
            .then(() => {
                // Silent success - no console logging
            })
            .catch(error => {
                // Only log if it's not a 419 error
                if (error.response?.status !== 419) {
                    console.error('Failed to refresh CSRF token via unhandled rejection handler:', error);
                }
            });
    }
});

// Global error handler to suppress 419 console errors
window.addEventListener('error', (event) => {
    // Check if this is a 419 error from network requests
    if (event.error?.response?.status === 419 ||
        event.message?.includes('419') ||
        event.filename?.includes('login') && event.message?.includes('419') ||
        event.message?.includes('unknown status') ||
        event.message?.includes('sawang-calero.localhost:8000/login')) {
        event.preventDefault();
        return false;
    }
});

// Additional error suppression for network errors - but only for unhandled cases
window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;

    // Only suppress if it's a 419 error that hasn't been handled by our interceptors
    // Check if the error has already been processed by our CSRF handlers
    if (reason?.response?.status === 419 &&
        !reason?.config?._retry && // Not a retry attempt
        !reason?.silent && // Not already marked as silent
        !reason?.suppressConsole) { // Not already marked for suppression

        // Only suppress if it's a login request or contains specific patterns
        if (reason?.config?.url?.includes('/login') ||
            reason?.message?.includes('unknown status') ||
            reason?.message?.includes('sawang-calero.localhost:8000/login')) {
            event.preventDefault();
            return false;
        }
    }
});


// Override console.error to filter out 419 errors
const originalConsoleError = console.error;
console.error = (...args) => {
    const message = args.join(' ');
    // Suppress 419 errors from console
    if (message.includes('419') ||
        message.includes('CSRF') && message.includes('token') ||
        message.includes('POST') && message.includes('419') ||
        message.includes('unknown status') ||
        message.includes('sawang-calero.localhost:8000/login')) {
        return; // Don't log 419 errors
    }
    // Log all other errors normally
    originalConsoleError.apply(console, args);
};

// Override console.warn to filter out 419 errors
const originalConsoleWarn = console.warn;
console.warn = (...args) => {
    const message = args.join(' ');
    // Suppress 419 warnings from console
    if (message.includes('419') ||
        message.includes('CSRF') && message.includes('token') ||
        message.includes('POST') && message.includes('419') ||
        message.includes('unknown status') ||
        message.includes('sawang-calero.localhost:8000/login')) {
        return; // Don't log 419 warnings
    }
    // Log all other warnings normally
    originalConsoleWarn.apply(console, args);
};

// Override console.log to filter out 419 errors
// const originalConsoleLog = console.log;
// console.log = (...args) => {
//     const message = args.join(' ');
//     // Suppress 419 logs from console
//     if (message.includes('419') ||
//         message.includes('CSRF') && message.includes('token') ||
//         message.includes('POST') && message.includes('419') ||
//         message.includes('unknown status') ||
//         message.includes('sawang-calero.localhost:8000/login')) {
//         return; // Don't log 419 errors
//     }
//     // Log all other logs normally
//     originalConsoleLog.apply(console, args);
// };

// Override console.info to filter out 419 errors
const originalConsoleInfo = console.info;
console.info = (...args) => {
    const message = args.join(' ');
    // Suppress 419 info from console
    if (message.includes('419') ||
        message.includes('CSRF') && message.includes('token') ||
        message.includes('POST') && message.includes('419') ||
        message.includes('unknown status') ||
        message.includes('sawang-calero.localhost:8000/login')) {
        return; // Don't log 419 errors
    }
    // Log all other info normally
    originalConsoleInfo.apply(console, args);
};

// Override console.debug to filter out 419 errors
const originalConsoleDebug = console.debug;
console.debug = (...args) => {
    const message = args.join(' ');
    // Suppress 419 debug from console
    if (message.includes('419') ||
        message.includes('CSRF') && message.includes('token') ||
        message.includes('POST') && message.includes('419') ||
        message.includes('unknown status') ||
        message.includes('sawang-calero.localhost:8000/login')) {
        return; // Don't log 419 errors
    }
    // Log all other debug normally
    originalConsoleDebug.apply(console, args);
};

// Utility functions for testing and debugging CSRF handler
window.csrfUtils = {
    getCurrentToken: () => globalCsrfToken,
    refreshToken: () => refreshCsrfToken(),
    debugCSRF: () => {
        const metaTag = document.querySelector('meta[name="csrf-token"]');
        // console.log('CSRF Debug Info:', {
        //     globalToken: globalCsrfToken,
        //     metaTagContent: metaTag?.content,
        //     axiosHeader: axios.defaults.headers.common['X-CSRF-TOKEN'],
        //     isRefreshing: isRefreshingCsrf,
        //     errorCount: csrfErrorCount,
        //     sessionId: document.cookie.split(';').find(c => c.trim().startsWith('laravel_session='))?.split('=')[1]
        // });
    },
    testAxiosRequest: async (url = '/superadmin/test-csrf', method = 'POST') => {
        try {
            const response = await axios({ method, url, data: { test: true } });
            // console.log('Axios CSRF test successful:', response.status);
            return response;
        } catch (error) {
            console.error('Axios CSRF test failed:', error);
            throw error;
        }
    },
    testFetchRequest: async (url = '/superadmin/test-csrf', method = 'POST') => {
        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': globalCsrfToken || document.querySelector('meta[name="csrf-token"]')?.content
                },
                body: JSON.stringify({ test: true }),
                credentials: 'same-origin'
            });
            // console.log('Fetch CSRF test successful:', response.status);
            return response;
        } catch (error) {
            console.error('Fetch CSRF test failed:', error);
            throw error;
        }
    },
    testCsrfToken: async () => {
        try {
            const response = await fetch('/csrf-token', {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                },
                credentials: 'same-origin'
            });
            const data = await response.json();
            // console.log('CSRF token endpoint test:', data);
            return data;
        } catch (error) {
            console.error('CSRF token endpoint test failed:', error);
            throw error;
        }
    },
    refreshCsrfToken: async () => {
        try {
            const response = await fetch('/csrf-refresh', {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                credentials: 'same-origin'
            });
            const data = await response.json();
            // console.log('CSRF refresh test:', data);
            return data;
        } catch (error) {
            console.error('CSRF refresh test failed:', error);
            throw error;
        }
    }
};

// Configure Ziggy to handle domain parameters globally
try {
    if (window.Ziggy) {
        const appDomain = (document.querySelector('meta[name="app-domain"]')?.content) || 'ekbt.org';
        const host = window.location.host; // e.g., sub.localhost:8000
        const hostname = window.location.hostname; // e.g., sawang-calero.ekbt.org or ekbt.org
        const parts = hostname.split('.');

        // Check if we're on the main domain (ekbt.org) or a subdomain (sawang-calero.ekbt.org)
        const isCentral = hostname === appDomain || host === appDomain ||
                         (host.endsWith(':'+window.location.port) && host.replace(':'+window.location.port, '') === appDomain);

        let subdomain = null;
        if (!isCentral && parts.length >= 2) {
            // Extract subdomain (first part before the main domain)
            subdomain = parts[0];
        }

        // On central host, remove tenant-domain variants to avoid Ziggy requiring {subdomain}
        if (isCentral && window.Ziggy.routes) {
            const filtered = {};
            Object.keys(window.Ziggy.routes).forEach((name) => {
                const r = window.Ziggy.routes[name];
                const hasTenantDomainParam = typeof r.domain === 'string' && r.domain.includes('{subdomain}.');
                if (!hasTenantDomainParam) filtered[name] = r;
            });
            window.Ziggy.routes = filtered;
        }

        window.Ziggy.url = window.location.origin;
        window.Ziggy.location = window.location.href;
        // Only set subdomain default if we're actually on a subdomain
        window.Ziggy.defaults = subdomain ? { subdomain } : {};

        // Expose a smart global route() helper that ensures subdomain is always provided for tenant routes
        const smartRoute = (name, params = {}, absolute = false) => {
            try {
                const def = window.Ziggy.routes?.[name];
                if (!def) return route(name, params, absolute, window.Ziggy);

                // Check if this route requires subdomain parameter
                const domainParams = def.parameters || [];
                const needsSubdomain = domainParams.includes('subdomain');

                // Debug logging (remove after testing)
                // if (name === 'announcements.destroy') {
                //     console.log('Route debug:', {
                //         name,
                //         params,
                //         domainParams,
                //         needsSubdomain,
                //         subdomain,
                //         def
                //     });
                // }

                // If we're on a tenant domain and route needs subdomain, ensure it's provided
                if (needsSubdomain && subdomain) {
                    let finalParams;

                    if (typeof params === 'object' && !Array.isArray(params)) {
                        // Object params: merge with subdomain
                        finalParams = { subdomain, ...params };
                    } else if (typeof params === 'string' || typeof params === 'number') {
                        // Scalar params: map to first non-subdomain parameter
                        const pathParams = domainParams.filter(p => p !== 'subdomain');
                        finalParams = { subdomain };
                        if (pathParams.length > 0) {
                            finalParams[pathParams[0]] = params;
                        }
                    } else {
                        // Array or other: just add subdomain
                        finalParams = { subdomain };
                    }

                    // Debug logging (remove after testing)
                    // if (name === 'announcements.destroy') {
                    //     console.log('Final params for announcements.destroy:', finalParams);
                    //     const testUrl = route(name, finalParams, absolute, window.Ziggy);
                    //     console.log('Generated URL:', testUrl);
                    // }

                    return route(name, finalParams, absolute, window.Ziggy);
                }

                return route(name, params, absolute, window.Ziggy);
            } catch (e) {
                if (import.meta.env.DEV) console.warn('Route generation failed:', e);
                return route(name, params, absolute, window.Ziggy);
            }
        };

        window.route = smartRoute;
    }
} catch (e) {
    if (import.meta.env.DEV) console.warn('Ziggy configuration skipped:', e);
}
